import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import ModalV2 from 'components/ModalV2';
import Tooltip from 'components/Tooltip';
import CreditMethodEducation from './CreditMethodEducation';
import {
  Container,
  HeaderContainer,
  ImportantNoteBox,
  OptionCard,
  OptionTitle,
  OptionDescription,
  OptionList,
  OptionListItem,
  SectionTitle,
  MethodCards,
  MethodCard,
  MethodIcon,
  MethodTitle,
  MethodDesc,
  MethodHighlight,
  TicketSectionContainer,
  TicketSectionTitle,
  TicketList,
  TicketItem,
  SelectionCircle,
  TicketInfo,
  TicketName,
  TicketSerialRow,
  SerialNumber,
  TicketPrice,
  ProcessingFeesSection,
  ProcessingFeesLeft,
  Checkbox,
  ProcessingFeesText,
  ProcessingFeesTitle,
  ProcessingFeesDesc,
  ProcessingFeesAmount,
  ProcessingFeesRight,
  TotalRow,
  CustomAmountSection,
  InfoBanner,
  InputContainer,
  DollarSign,
  AmountInput,
  OriginalAmount,
  CreditTotalLabel,
  CreditTotalAmount,
  ModalContent,
  ModalHeader,
  ModalIconWrapper,
  ModalIcon,
  ModalTitle,
  ModalSubtitle,
  ModalBreakdown,
  ModalBreakdownItem,
  ModalBreakdownIcon,
  ModalBreakdownInfo,
  ModalBreakdownLabel,
  ModalBreakdownTitle,
  ModalBreakdownDescription,
  ModalBreakdownAmount,
  ModalDivider,
  ModalTotal,
  ModalTotalLabel,
  ModalTotalAmount,
  ModalButtonWrapper,
} from './StyledComponents';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

// Helper function to get text based on adjustment type and scenario
const getAdjustmentText = (type, key, scenario = 'normal_refund') => {
  const ticketTextCap =
    getTicketText(2).charAt(0).toUpperCase() + getTicketText(2).slice(1);
  const texts = {
    credit: {
      question: 'How would you like to issue the credit?',
      fullTitle: `Full ${
        getTicketText(1).charAt(0).toUpperCase() + getTicketText(1).slice(1)
      } Credit`,
      fullDesc: `Select specific ${getTicketText(2)} to credit.`,
      partialTitle: 'Partial Credit',
      partialDesc: 'Issue a custom amount.',
      selectTickets: `Select ${ticketTextCap} to Credit`,
      enterAmount: 'Enter Credit Amount',
      amountLabel: 'Credit Amount',
    },
    refund: {
      question: 'How would you like to issue the refund?',
      fullTitle: `Full ${
        getTicketText(1).charAt(0).toUpperCase() + getTicketText(1).slice(1)
      } Refund`,
      fullDesc: `Select specific ${getTicketText(2)} to refund.`,
      partialTitle: 'Partial Refund',
      partialDesc: 'Issue a custom amount.',
      selectTickets: `Select ${ticketTextCap} to Refund`,
      enterAmount: 'Enter Refund Amount',
      amountLabel: 'Refund Amount',
    },
    full_credit: {
      question: 'How would you like to issue the refund?',
      fullTitle: `Full ${
        getTicketText(1).charAt(0).toUpperCase() + getTicketText(1).slice(1)
      } Refund`,
      fullDesc: `Select specific ${getTicketText(2)} to refund.`,
      partialTitle: 'Partial Refund',
      partialDesc: 'Issue a custom amount.',
      selectTickets: `Select ${ticketTextCap} to Refund`,
      enterAmount: 'Enter Refund Amount',
      amountLabel: 'Credit Amount', // Changed to Credit Amount for full credit restoration
    },
    mixed: {
      question: 'How would you like to issue the refund?',
      fullTitle: `Full ${
        getTicketText(1).charAt(0).toUpperCase() + getTicketText(1).slice(1)
      } Refund`,
      fullDesc: `Select specific ${getTicketText(2)} to refund.`,
      partialTitle: 'Partial Refund',
      partialDesc: 'Issue a custom amount.',
      selectTickets: `Select ${ticketTextCap} to Refund`,
      enterAmount: 'Enter Refund Amount',
      amountLabel: 'Total Amount', // Show Total Amount for mixed scenarios
    },
  };

  // For refund type, use scenario to determine which text to use
  if (type === 'refund' && scenario !== 'normal_refund') {
    return texts[scenario]?.[key] || texts.refund[key];
  }

  return texts[type]?.[key] || texts.credit[key];
};

const Step1SelectCreditMethod = ({
  creditData,
  setCreditData,
  type = 'credit',
  refundEstimateLoading,
}) => {
  const theme = useTheme();
  const [showEducation, setShowEducation] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);

  const handleMethodSelect = (method) => {
    setCreditData((prev) => ({
      ...prev,
      creditMethod: method,
      selectedTickets: method === 'tickets' ? [] : prev.selectedTickets,
      customAmount: method === 'custom' ? prev.customAmount : '',
    }));
  };

  const handleTicketToggle = (ticket) => {
    if (creditData.creditMethod !== 'tickets') return;

    const isSelected = creditData.selectedTickets.some(
      (t) => t.id === ticket.id
    );

    if (isSelected) {
      setCreditData((prev) => ({
        ...prev,
        selectedTickets: prev.selectedTickets.filter((t) => t.id !== ticket.id),
      }));
    } else {
      setCreditData((prev) => ({
        ...prev,
        selectedTickets: [...prev.selectedTickets, ticket],
      }));
    }
  };

  const handleProcessingFeesToggle = () => {
    setCreditData((prev) => ({
      ...prev,
      includeProcessingFees: !prev.includeProcessingFees,
    }));
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCreditData((prev) => ({
      ...prev,
      customAmount: value,
    }));
  };

  // Calculate the total credit amount
  const calculateCreditTotal = () => {
    if (creditData.creditMethod === 'custom') {
      return parseFloat(creditData.customAmount) || 0;
    }

    if (creditData.creditMethod === 'tickets') {
      const refundAmount = parseFloat(creditData.dynamicRefundAmount) || 0;
      const creditAmount = parseFloat(creditData.creditRestored) || 0;

      // For mixed scenarios, return the sum of both
      // For full credit restoration, return just the credit amount
      // For normal refunds, return just the refund amount
      if (creditAmount > 0 && refundAmount > 0) {
        // Mixed scenario: both credit and refund
        return creditAmount + refundAmount;
      } else if (creditAmount > 0) {
        // Full credit restoration
        return creditAmount;
      } else {
        // Normal refund
        return refundAmount;
      }
    }

    return 0;
  };

  const creditTotal = calculateCreditTotal();

  // Determine the scenario for dynamic text
  const getScenario = () => {
    if (type !== 'refund') return 'normal_refund';

    const credit = parseFloat(creditData.creditRestored) || 0;
    const refund = parseFloat(creditData.dynamicRefundAmount) || 0;

    if (credit > 0 && refund === 0) {
      return 'full_credit';
    } else if (credit > 0 && refund > 0) {
      return 'mixed';
    }
    return 'normal_refund';
  };

  const scenario = getScenario();

  // Check if custom refund should be disabled (for full credit restoration scenarios)
  const isCustomRefundDisabled = () => {
    if (type !== 'refund') return false;

    // Check if transaction was paid 100% with credit from transaction data
    // This check works immediately, even before tickets are selected
    if (creditData.transactionInfo) {
      const transactionTotal = creditData.transactionInfo.total || 0;
      const creditUsed = creditData.transactionInfo.creditUsed || 0;

      // If credit used equals or exceeds total, it's a credit-only purchase
      if (creditUsed > 0 && creditUsed >= transactionTotal) {
        return true;
      }
    }

    // Also disable if refund estimate shows it's credit-only (when tickets are selected)
    return scenario === 'full_credit';
  };

  const customRefundDisabled = isCustomRefundDisabled();

  // Show info modal when scenario is full_credit or mixed (only once)
  // This triggers when tickets are selected and refund estimate shows credit restoration
  useEffect(() => {
    if (
      type === 'refund' &&
      !hasShownModal &&
      (scenario === 'full_credit' || scenario === 'mixed')
    ) {
      setShowInfoModal(true);
      setHasShownModal(true);
    }
  }, [scenario, type, hasShownModal]);

  // Force tickets method if custom refund is disabled and custom is currently selected
  useEffect(() => {
    if (customRefundDisabled && creditData.creditMethod === 'custom') {
      setCreditData((prev) => ({
        ...prev,
        creditMethod: 'tickets',
        selectedTickets: [],
      }));
    }
  }, [customRefundDisabled, creditData.creditMethod, setCreditData]);

  // Update creditData.amount whenever the total changes
  useEffect(() => {
    setCreditData((prev) => ({
      ...prev,
      amount: creditTotal.toFixed(2),
    }));
  }, [creditTotal, setCreditData]);

  return (
    <Container>
      <HeaderContainer>
        <SectionTitle>
          {getAdjustmentText(type, 'question', scenario)}
        </SectionTitle>
        <CreditMethodEducation
          type={type}
          isOpen={showEducation}
          onOpen={() => setShowEducation(true)}
          onClose={() => setShowEducation(false)}
          OptionCard={OptionCard}
          OptionTitle={OptionTitle}
          OptionDescription={OptionDescription}
          OptionList={OptionList}
          OptionListItem={OptionListItem}
          ImportantNoteBox={ImportantNoteBox}
        />
      </HeaderContainer>

      <MethodCards>
        <MethodCard
          selected={creditData.creditMethod === 'tickets'}
          onClick={() => handleMethodSelect('tickets')}
        >
          <MethodIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </MethodIcon>
          <MethodTitle>{getAdjustmentText(type, 'fullTitle')}</MethodTitle>
          <MethodDesc>{getAdjustmentText(type, 'fullDesc')}</MethodDesc>
          <MethodHighlight invalidate>
            {getTicketText(2).charAt(0).toUpperCase() +
              getTicketText(2).slice(1)}{' '}
            will be invalidated.
          </MethodHighlight>
        </MethodCard>

        {customRefundDisabled ? (
          <Tooltip
            content="Partial refunds are not available for purchases paid entirely with account credit. Please select specific tickets to refund and restore the credit to the customer's account."
            placement="top"
            maxWidth="320px"
            preservePosition={true}
          >
            <MethodCard
              selected={creditData.creditMethod === 'custom'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={customRefundDisabled}
            >
              <MethodIcon
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </MethodIcon>
              <MethodTitle>
                {getAdjustmentText(type, 'partialTitle')}
              </MethodTitle>
              <MethodDesc>Not available for credit-only purchases</MethodDesc>
            </MethodCard>
          </Tooltip>
        ) : (
          <MethodCard
            selected={creditData.creditMethod === 'custom'}
            onClick={() => handleMethodSelect('custom')}
            disabled={customRefundDisabled}
          >
            <MethodIcon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </MethodIcon>
            <MethodTitle>{getAdjustmentText(type, 'partialTitle')}</MethodTitle>
            <MethodDesc>{getAdjustmentText(type, 'partialDesc')}</MethodDesc>
            <MethodHighlight>
              {getTicketText(2).charAt(0).toUpperCase() +
                getTicketText(2).slice(1)}{' '}
              remain valid.
            </MethodHighlight>
          </MethodCard>
        )}
      </MethodCards>

      {creditData.creditMethod === 'tickets' && (
        <>
          <TicketSectionContainer>
            <TicketSectionTitle>
              {getAdjustmentText(type, 'selectTickets')}
            </TicketSectionTitle>

            {/* For TicketHoss: show flat ticket list */}
            {process.env.REACT_APP_PLATFORM === 'tickethoss' && (
              <TicketList>
                {creditData.transactionInfo?.tickets
                  ?.filter((ticket) => {
                    // For refunds: show unused tickets and credited tickets (can convert credit to refund)
                    if (type === 'refund') {
                      // Credited tickets have both refunded:true and credited:true
                      // So we need to include them by checking if credited OR if not refunded
                      return ticket.credited || !ticket.refunded;
                    }
                    // For credits: show only unused tickets (not refunded or credited)
                    return !ticket.refunded && !ticket.credited;
                  })
                  .map((ticket) => {
                    const isSelected = creditData.selectedTickets.some(
                      (t) => t.id === ticket.id
                    );
                    return (
                      <TicketItem
                        key={ticket.id}
                        selected={isSelected}
                        onClick={() => handleTicketToggle(ticket)}
                      >
                        <SelectionCircle selected={isSelected} />
                        <TicketInfo>
                          <TicketName>{ticket.name}</TicketName>
                          {/* Show section/row/seat if available */}
                          {ticket.section && (
                            <TicketSerialRow>
                              <SerialNumber style={{ fontSize: '13px' }}>
                                {ticket.section}
                                {ticket.row && ` | Row: ${ticket.row}`}
                                {ticket.seat && ` | Seat: ${ticket.seat}`}
                              </SerialNumber>
                            </TicketSerialRow>
                          )}
                          {ticket.serialNumber && (
                            <TicketSerialRow>
                              <SerialNumber>
                                {ticket.serialNumber.length > 4
                                  ? ticket.serialNumber.slice(0, -4)
                                  : ticket.serialNumber}
                              </SerialNumber>
                              {ticket.serialNumber.length > 4 && (
                                <Badge
                                  variant="dark"
                                  size="small"
                                  shape="square"
                                  uppercase
                                >
                                  {ticket.serialNumber.slice(-4)}
                                </Badge>
                              )}
                              {/* Show status badge inline with serial number */}
                              {(ticket.refunded ||
                                ticket.credited ||
                                (ticket.scan_logs &&
                                  ticket.scan_logs.length > 0)) && (
                                <>
                                  {ticket.credited ? (
                                    <Badge
                                      variant="accent"
                                      uppercase
                                      size="small"
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                        </svg>
                                      }
                                    >
                                      Credited
                                    </Badge>
                                  ) : ticket.refunded ? (
                                    <Badge
                                      variant="accent"
                                      uppercase
                                      size="small"
                                      icon={
                                        <svg
                                          width="16"
                                          height="16"
                                          fill="currentColor"
                                          viewBox="0 0 16 16"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                          />
                                        </svg>
                                      }
                                    >
                                      Refunded
                                    </Badge>
                                  ) : (
                                    ticket.scan_logs &&
                                    ticket.scan_logs.length > 0 && (
                                      <Badge
                                        variant="accent"
                                        uppercase
                                        size="small"
                                        icon={
                                          <svg
                                            width="16"
                                            height="16"
                                            fill="currentColor"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                          </svg>
                                        }
                                      >
                                        Scanned
                                      </Badge>
                                    )
                                  )}
                                </>
                              )}
                            </TicketSerialRow>
                          )}
                        </TicketInfo>
                        <TicketPrice>${ticket.price.toFixed(2)}</TicketPrice>
                      </TicketItem>
                    );
                  })}
              </TicketList>
            )}

            {/* For non-TicketHoss (Pit Pay): show tickets grouped by user */}
            {process.env.REACT_APP_PLATFORM !== 'tickethoss' && (
              <>
                {creditData.transactionInfo?.users?.map(
                  (userGroup, userIndex) => {
                    // Filter tickets for this user
                    const filteredTickets = userGroup.tickets.filter(
                      (ticket) => {
                        // For refunds: show unused tickets and credited tickets (can convert credit to refund)
                        if (type === 'refund') {
                          return ticket.credited || !ticket.refunded;
                        }
                        // For credits: show only unused tickets (not refunded or credited)
                        return !ticket.refunded && !ticket.credited;
                      }
                    );

                    // Skip this user if they have no valid tickets
                    if (filteredTickets.length === 0) return null;

                    return (
                      <div key={userIndex}>
                        {/* User header */}
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            marginTop: userIndex > 0 ? '24px' : '0',
                            marginBottom: '12px',
                            color:
                              theme?.colors?.textGray?.secondary || '#64748b',
                          }}
                        >
                          {userGroup.user?.name || 'User'}{' '}
                          {userGroup.user?.email && `(${userGroup.user.email})`}
                        </div>

                        <TicketList>
                          {filteredTickets.map((ticket) => {
                            const isSelected = creditData.selectedTickets.some(
                              (t) => t.id === ticket.id
                            );
                            return (
                              <TicketItem
                                key={ticket.id}
                                selected={isSelected}
                                onClick={() => handleTicketToggle(ticket)}
                              >
                                <SelectionCircle selected={isSelected} />
                                <TicketInfo>
                                  <TicketName>{ticket.name}</TicketName>
                                  {/* Show section/row/seat if available */}
                                  {ticket.section && (
                                    <TicketSerialRow>
                                      <SerialNumber
                                        style={{ fontSize: '13px' }}
                                      >
                                        {ticket.section}
                                        {ticket.row && ` | Row: ${ticket.row}`}
                                        {ticket.seat &&
                                          ` | Seat: ${ticket.seat}`}
                                      </SerialNumber>
                                    </TicketSerialRow>
                                  )}
                                  {/* No serial number for non-tickethoss */}
                                  {/* Show status badges */}
                                  {(ticket.refunded ||
                                    ticket.credited ||
                                    (ticket.scan_logs &&
                                      ticket.scan_logs.length > 0)) && (
                                    <TicketSerialRow>
                                      {ticket.credited ? (
                                        <Badge
                                          variant="accent"
                                          uppercase
                                          size="small"
                                          icon={
                                            <svg
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              viewBox="0 0 16 16"
                                            >
                                              <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                                            </svg>
                                          }
                                        >
                                          Credited
                                        </Badge>
                                      ) : ticket.refunded ? (
                                        <Badge
                                          variant="accent"
                                          uppercase
                                          size="small"
                                          icon={
                                            <svg
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              viewBox="0 0 16 16"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 1 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
                                              />
                                            </svg>
                                          }
                                        >
                                          Refunded
                                        </Badge>
                                      ) : (
                                        ticket.scan_logs &&
                                        ticket.scan_logs.length > 0 && (
                                          <Badge
                                            variant="accent"
                                            uppercase
                                            size="small"
                                            icon={
                                              <svg
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                              </svg>
                                            }
                                          >
                                            Scanned
                                          </Badge>
                                        )
                                      )}
                                    </TicketSerialRow>
                                  )}
                                </TicketInfo>
                                <TicketPrice>
                                  ${ticket.price.toFixed(2)}
                                </TicketPrice>
                              </TicketItem>
                            );
                          })}
                        </TicketList>
                      </div>
                    );
                  }
                )}
              </>
            )}
          </TicketSectionContainer>

          {/* Show Processing Fees checkbox if show_fee is true */}
          {creditData.transactionInfo?.show_fee !== false ? (
            <ProcessingFeesSection onClick={handleProcessingFeesToggle}>
              <ProcessingFeesLeft>
                <Checkbox checked={creditData.includeProcessingFees} />
                <ProcessingFeesText>
                  <ProcessingFeesTitle>
                    Include Processing Fees
                  </ProcessingFeesTitle>
                  <ProcessingFeesDesc>
                    {creditData.selectedTickets.length > 0
                      ? `Processing fees for ${
                          creditData.selectedTickets.length
                        } selected ${getTicketText(
                          creditData.selectedTickets.length
                        )}`
                      : `Select ${getTicketText(2)} to see processing fees`}
                  </ProcessingFeesDesc>
                </ProcessingFeesText>
              </ProcessingFeesLeft>
              <ProcessingFeesRight>
                <ProcessingFeesAmount>
                  {refundEstimateLoading ? (
                    <span style={{ fontSize: '14px' }}>Loading...</span>
                  ) : (
                    <>
                      $
                      {(
                        creditData.dynamicProcessingFees ||
                        creditData.transactionInfo?.processingFees ||
                        0
                      ).toFixed(2)}
                    </>
                  )}
                </ProcessingFeesAmount>
                {creditTotal > 0 && (
                  <TotalRow>
                    <CreditTotalLabel>
                      {getAdjustmentText(type, 'amountLabel', scenario)}
                    </CreditTotalLabel>
                    <CreditTotalAmount>
                      ${creditTotal.toFixed(2)}
                    </CreditTotalAmount>
                  </TotalRow>
                )}
              </ProcessingFeesRight>
            </ProcessingFeesSection>
          ) : (
            /* Show only the total when fees are hidden */
            creditTotal > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '20px',
                }}
              >
                <TotalRow
                  style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}
                >
                  <CreditTotalLabel>
                    {getAdjustmentText(type, 'amountLabel', scenario)}
                  </CreditTotalLabel>
                  <CreditTotalAmount>
                    ${creditTotal.toFixed(2)}
                  </CreditTotalAmount>
                </TotalRow>
              </div>
            )
          )}
        </>
      )}

      {creditData.creditMethod === 'custom' && (
        <CustomAmountSection>
          <TicketSectionTitle>
            {getAdjustmentText(type, 'enterAmount')}
          </TicketSectionTitle>
          <InfoBanner>
            <svg
              style={{ width: '18px', height: '18px' }}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              {getTicketText(2).charAt(0).toUpperCase() +
                getTicketText(2).slice(1)}{' '}
              will remain valid and can still be used for entry
            </span>
          </InfoBanner>
          <InputContainer>
            <DollarSign>$</DollarSign>
            <AmountInput
              type="text"
              placeholder="0.00"
              value={creditData.customAmount}
              onChange={handleCustomAmountChange}
            />
          </InputContainer>
          <OriginalAmount>
            Original transaction amount: $
            {creditData.transactionInfo?.total?.toFixed(2) || '0.00'}
          </OriginalAmount>
        </CustomAmountSection>
      )}

      {/* Show credit total for custom amount method */}
      {creditData.creditMethod === 'custom' && creditTotal > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '0 20px 20px',
          }}
        >
          <TotalRow style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
            <CreditTotalLabel>
              {getAdjustmentText(type, 'amountLabel', scenario)}
            </CreditTotalLabel>
            <CreditTotalAmount>${creditTotal.toFixed(2)}</CreditTotalAmount>
          </TotalRow>
        </div>
      )}

      {/* Info Modal for Credit Restoration */}
      <ModalV2 isOpen={showInfoModal} onClose={() => setShowInfoModal(false)}>
        <ModalContent
          style={{
            background: theme?.name === 'dark' ? '#1E293B' : '#ffffff',
            borderRadius: '12px',
            boxShadow: theme?.shadows?.xl || '0 8px 32px rgba(0, 0, 0, 0.12)',
            maxWidth: '500px',
          }}
        >
          {/* Header */}
          <ModalHeader>
            <ModalIconWrapper>
              {scenario === 'full_credit' ? (
                <ModalIcon fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                </ModalIcon>
              ) : (
                <ModalIcon
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </ModalIcon>
              )}
            </ModalIconWrapper>
            <ModalTitle>
              {scenario === 'full_credit'
                ? 'Credit Restoration'
                : 'Mixed Refund & Credit'}
            </ModalTitle>
            <ModalSubtitle>
              {scenario === 'full_credit'
                ? 'This purchase was paid entirely with account credit'
                : 'This purchase used multiple payment methods'}
            </ModalSubtitle>
          </ModalHeader>

          {/* Content */}
          {scenario === 'full_credit' ? (
            <ModalBreakdown>
              <ModalBreakdownItem single>
                <ModalBreakdownIcon large type="credit">
                  <svg
                    width="28"
                    height="28"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                  </svg>
                </ModalBreakdownIcon>
                <ModalBreakdownInfo>
                  <ModalBreakdownLabel uppercase>
                    Refund Type
                  </ModalBreakdownLabel>
                  <ModalBreakdownTitle large hasDescription>
                    Account Credit
                  </ModalBreakdownTitle>
                  <ModalBreakdownDescription>
                    Will be immediately restored to customer's account
                  </ModalBreakdownDescription>
                </ModalBreakdownInfo>
                <ModalBreakdownAmount large type="credit">
                  ${creditData.creditRestored?.toFixed(2) || '0.00'}
                </ModalBreakdownAmount>
              </ModalBreakdownItem>
            </ModalBreakdown>
          ) : (
            <>
              <ModalBreakdown>
                <ModalBreakdownItem type="credit">
                  <ModalBreakdownIcon type="credit">
                    <svg
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
                    </svg>
                  </ModalBreakdownIcon>
                  <ModalBreakdownInfo>
                    <ModalBreakdownLabel>Account Credit</ModalBreakdownLabel>
                    <ModalBreakdownTitle>
                      Credit Restoration
                    </ModalBreakdownTitle>
                  </ModalBreakdownInfo>
                  <ModalBreakdownAmount type="credit">
                    ${creditData.creditRestored?.toFixed(2) || '0.00'}
                  </ModalBreakdownAmount>
                </ModalBreakdownItem>

                <ModalBreakdownItem type="refund">
                  <ModalBreakdownIcon type="refund">
                    <svg
                      width="24"
                      height="24"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z" />
                    </svg>
                  </ModalBreakdownIcon>
                  <ModalBreakdownInfo>
                    <ModalBreakdownLabel>Payment Method</ModalBreakdownLabel>
                    <ModalBreakdownTitle>
                      Card Refund (3-5 days)
                    </ModalBreakdownTitle>
                  </ModalBreakdownInfo>
                  <ModalBreakdownAmount type="refund">
                    ${creditData.dynamicRefundAmount?.toFixed(2) || '0.00'}
                  </ModalBreakdownAmount>
                </ModalBreakdownItem>
              </ModalBreakdown>

              <ModalDivider />

              <ModalTotal>
                <ModalTotalLabel>Total Refund</ModalTotalLabel>
                <ModalTotalAmount>
                  $
                  {(
                    parseFloat(creditData.creditRestored || 0) +
                    parseFloat(creditData.dynamicRefundAmount || 0)
                  ).toFixed(2)}
                </ModalTotalAmount>
              </ModalTotal>
            </>
          )}

          <ModalButtonWrapper>
            <ButtonV2 onClick={() => setShowInfoModal(false)}>
              Got it, Continue
            </ButtonV2>
          </ModalButtonWrapper>
        </ModalContent>
      </ModalV2>
    </Container>
  );
};

Step1SelectCreditMethod.propTypes = {
  creditData: PropTypes.object.isRequired,
  setCreditData: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default Step1SelectCreditMethod;
